import type { Booking, Deliverable, DeliverableStep, FrozenDeliverable, ISODate, Period, Series } from "./types";
import { periodIndexOf } from "./calendar";
import { stepShares } from "./chains";

/**
 * Rule 7: forecast uses the earned date when earned, else the forecast date.
 * Cesar, Sep 27 test video: "the plan and forecast match in the beginning", so a milestone with no forecast date is
 * forecast on its planned date.
 */
export function stepDate(s: DeliverableStep, series: Series): ISODate | null {
  if (series === "planned") return s.planned ?? null;
  if (series === "earned") return s.earned ?? null;
  return s.earned ?? s.forecast ?? s.planned ?? null;
}

/** Rule 17: weight in effect at a period = the latest change order effective on or before it, else the original weight. */
export function weightAt(d: Deliverable, periods: Period[], idx: number): number {
  let w = d.weight;
  if (!d.weightChanges?.length) return w;
  const num = periods[idx]?.number ?? Infinity;
  const sorted = [...d.weightChanges].sort((a, b) => a.periodNumber - b.periodNumber);
  for (const c of sorted) if (c.periodNumber <= num) w = c.weight;
  return w;
}

export function currentWeight(d: Deliverable): number {
  if (!d.weightChanges?.length) return d.weight;
  return [...d.weightChanges].sort((a, b) => a.periodNumber - b.periodNumber)[d.weightChanges.length - 1].weight;
}

/**
 * Book one deliverable's value for one series into periods.
 *
 * Rule 7: at a cutoff, the cumulative value = weight x cumulative % of the last milestone (in chain order) whose date
 * is on or before the cutoff.
 * Rule 16/17: history is never restated. A period only books when a new milestone is reached; the cumulative value
 * then becomes (weight in effect in that period) x (its cumulative %). A weight change with no new milestone books
 * nothing, which produces the sawtooth on % curves. Any catch-up from a weight change is attributed back to the
 * already-reached steps in proportion to their shares, so step-level grouping (e.g. by PBS) stays consistent.
 * Dates before the calendar start are booked in the first period and dates after its end are never booked; both are
 * reported as errors by validate().
 */
export function bookSeries(
  d: Deliverable,
  periods: Period[],
  series: Series,
  frozen?: { lastClosedIdx: number; state: FrozenDeliverable },
): BookResult {
  const n = periods.length;
  const steps = d.steps;
  const shares = stepShares(steps);
  const bestAt = new Int32Array(n).fill(0);
  steps.forEach((s, j) => {
    const dt = stepDate(s, series);
    if (!dt) return;
    const pi = periodIndexOf(periods, dt);
    const idx = pi === "before" ? 0 : pi === "after" ? -1 : pi;
    if (idx >= 0 && bestAt[idx] < j + 1) bestAt[idx] = j + 1;
  });

  let reached = 0;
  let booked = 0;
  const bookings: Booking[] = [];
  const perStep = new Float64Array(steps.length);
  let startIdx = 0;
  if (frozen) {
    for (const b of frozen.state.bookings[series]) {
      if (b.periodIdx <= frozen.lastClosedIdx) {
        bookings.push(b);
        booked += b.value;
        perStep[b.stepIdx] += b.value;
      }
    }
    reached = frozen.state.reached[series];
    startIdx = frozen.lastClosedIdx + 1;
  }

  const reachedByPeriod = new Int32Array(n);
  let running = 0;
  for (let p = 0; p < n; p++) {
    if (bestAt[p] > running) running = bestAt[p];
    if (p < startIdx || running <= reached) {
      reachedByPeriod[p] = reached;
      continue;
    }
    const w = weightAt(d, periods, p);
    const k0 = reached;
    const k1 = running;
    // Catch-up on steps already reached (only non-zero after a weight change).
    const cumK0 = k0 > 0 ? steps[k0 - 1].cum : 0;
    const catchUp = w * cumK0 - booked;
    if (Math.abs(catchUp) > 1e-9 * Math.max(1, Math.abs(w)) && cumK0 > 0) {
      for (let j = 0; j < k0; j++) {
        const v = (catchUp * shares[j]) / cumK0;
        if (v !== 0) bookings.push({ periodIdx: p, stepIdx: j, value: v });
      }
    }
    for (let j = k0; j < k1; j++) {
      const v = w * shares[j];
      if (v !== 0) bookings.push({ periodIdx: p, stepIdx: j, value: v });
    }
    booked = w * steps[k1 - 1].cum;
    reached = k1;
    reachedByPeriod[p] = reached;
  }
  return { bookings, reachedByPeriod };
}

export interface BookResult {
  bookings: Booking[];
  /** Steps reached (1-based count) after each period. For frozen periods this is the frozen count. */
  reachedByPeriod: Int32Array;
}
